import { type KeyboardEvent, type Ref, useEffect, useRef, useState } from "react";

import { $toggleLink } from "@lexical/link";
import { $removeList, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, $getSelection, $isRangeSelection, $isTextNode, FORMAT_TEXT_COMMAND, type TextFormatType, UNDO_COMMAND } from "lexical";

import type { RTEComponentDefs } from "../RichTextEditorProps";
import { type RTEActiveFormatDefs } from "../RichTextEditorProps";
import {
  BoldIcon,
  BulletedListIcon,
  ClearFormattingIcon,
  ItalicIcon,
  LinkIcon,
  NumberedListIcon,
  StrikethroughIcon,
  UnderlineIcon,
  UndoIcon,
} from "./Icons";
import { Toggle } from "./Toggle";

import CSS from "../styles.module.css";

const CLEAR_FORMATTING_TAG = "rte-clear-formatting";

export interface RTEToolbarDefs {
  readonly activeFormats: RTEActiveFormatDefs;
  readonly disabled: boolean;
  readonly onLinkButtonClick: () => void;
  readonly linkButtonRef?: Ref<HTMLButtonElement>;
}

const Spacer: RTEComponentDefs = () => {
  return <div aria-hidden="true" className={CSS.ToolbarSpacer} />;
};

const Divider: RTEComponentDefs = () => {
  return <hr aria-hidden="true" className={CSS.ToolbarDivider} />;
};

export const Toolbar: RTEComponentDefs<RTEToolbarDefs> = ({ activeFormats, disabled, onLinkButtonClick, linkButtonRef }) => {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [canUndoClear, setCanUndoClear] = useState(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ tags, dirtyElements, dirtyLeaves }) => {
      if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return;
      setCanUndoClear(tags.has(CLEAR_FORMATTING_TAG));
    });
  }, [editor]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!(["ArrowLeft", "ArrowRight", "Home", "End"] as string[]).includes(event.key)) return;
    const toolbar = toolbarRef.current;
    if (!toolbar) return;
    const buttons = Array.from(toolbar.querySelectorAll<HTMLButtonElement>("button:not(:disabled):not([disabled])"));
    if (buttons.length === 0) return;
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    let nextIndex: number;
    if (event.key === "ArrowLeft") {
      nextIndex = currentIndex <= 0 ? buttons.length - 1 : currentIndex - 1;
    } else if (event.key === "ArrowRight") {
      nextIndex = currentIndex >= buttons.length - 1 ? 0 : currentIndex + 1;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else {
      nextIndex = buttons.length - 1;
    }
    event.preventDefault();
    buttons[nextIndex]?.focus();
  };

  const makeListToggleHandler = (active: boolean, insertCommand: typeof INSERT_ORDERED_LIST_COMMAND) => () => {
    editor.dispatchCommand(active ? REMOVE_LIST_COMMAND : insertCommand, undefined);
  };

  const makeFormatToggleHandler = (format: TextFormatType) => () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const handleClearFormatting = () => {
    editor.update(
      () => {
        let selection = $getSelection();
        if (!$isRangeSelection(selection) || selection.isCollapsed()) {
          const root = $getRoot();
          selection = root.select(0, root.getChildrenSize());
        }
        if (!$isRangeSelection(selection)) return;
        for (const node of selection.getNodes()) {
          if ($isTextNode(node) && node.getFormat() !== 0) node.setFormat(0);
        }
        $toggleLink(null);
        $removeList();
      },
      { tag: CLEAR_FORMATTING_TAG },
    );
  };

  const handleUndoClear = () => {
    editor.dispatchCommand(UNDO_COMMAND, undefined);
  };

  return (
    <div
      aria-disabled={disabled || undefined}
      aria-label="Text formatting"
      className={CSS.ToolbarWrapper}
      onKeyDown={handleKeyDown}
      ref={toolbarRef}
      role="toolbar"
    >
      <div className={CSS.Toolbar}>
        <Toggle label="Bold" active={activeFormats.bold} disabled={disabled} onClick={makeFormatToggleHandler("bold")} tooltip="Bold">
          <BoldIcon />
        </Toggle>
        <Toggle
          label="Italic"
          active={activeFormats.italic}
          disabled={disabled}
          onClick={makeFormatToggleHandler("italic")}
          tooltip="Italic"
        >
          <ItalicIcon />
        </Toggle>
        <Toggle
          active={activeFormats.underline}
          disabled={disabled}
          label="Underline"
          onClick={makeFormatToggleHandler("underline")}
          tooltip="Underline"
        >
          <UnderlineIcon />
        </Toggle>
        <Toggle
          active={activeFormats.strikethrough}
          disabled={disabled}
          label="Strikethrough"
          onClick={makeFormatToggleHandler("strikethrough")}
          tooltip="Strikethrough"
        >
          <StrikethroughIcon />
        </Toggle>
        <Divider />
        <Toggle
          active={activeFormats.unorderedList}
          disabled={disabled}
          label="Bulleted list"
          onClick={makeListToggleHandler(activeFormats.unorderedList, INSERT_UNORDERED_LIST_COMMAND)}
          tooltip="Bulleted list"
        >
          <BulletedListIcon />
        </Toggle>
        <Toggle
          active={activeFormats.orderedList}
          disabled={disabled}
          label="Numbered list"
          onClick={makeListToggleHandler(activeFormats.orderedList, INSERT_ORDERED_LIST_COMMAND)}
          tooltip="Numbered list"
        >
          <NumberedListIcon />
        </Toggle>
        <Divider />
        <Toggle
          active={activeFormats.link}
          disabled={disabled}
          label={activeFormats.link ? "Edit link" : "Insert link"}
          onClick={onLinkButtonClick}
          tooltip={activeFormats.link ? "Update URL" : "Insert link"}
          buttonRef={linkButtonRef}
        >
          <LinkIcon />
        </Toggle>
        <Spacer />
        {canUndoClear ? (
          <Toggle role="button" label="Undo clear formatting" disabled={disabled} onClick={handleUndoClear} tooltip="Undo">
            <UndoIcon />
          </Toggle>
        ) : (
          <Toggle role="button" label="Clear formatting" disabled={disabled} onClick={handleClearFormatting} tooltip="Clear formatting">
            <ClearFormattingIcon />
          </Toggle>
        )}
      </div>
    </div>
  );
};
